// Edge lifecycle in the UI (WP3): enrollment state, id checks, VPN commands.
import type { Edge, LifecycleResult, VpnKey } from '../api/types.js';
import { formatDuration } from './format.js';

/** Certificates are renewed at 2/3 of 30 days; under 7 days left means renewal is failing. */
export const CERT_WARN_MS = 7 * 86_400_000;

/** Default install directory of deploy/edge/install.sh. */
export const EDGE_INSTALL_DIR = '/opt/kalvio-edge';

export type EnrollmentKind =
  'disabled' | 'not-enrolled' | 'token-issued' | 'enrolled' | 'expiring' | 'expired';

export interface EnrollmentState {
  kind: EnrollmentKind;
  label: string;
  /** Badge class suffix. */
  tone: 'online' | 'offline' | 'unknown' | 'warn';
}

export function enrollmentState(edge: Edge, now: number): EnrollmentState {
  if (edge.disabled_at != null) return { kind: 'disabled', label: 'Disabled', tone: 'offline' };
  const expires = edge.cert_expires_at ?? null;
  if (!edge.cert_serial) {
    return (edge.enrollment_token_expires_at ?? 0) > now
      ? { kind: 'token-issued', label: 'Waiting for install', tone: 'unknown' }
      : { kind: 'not-enrolled', label: 'Not enrolled yet', tone: 'unknown' };
  }
  if (expires !== null && expires <= now) {
    return { kind: 'expired', label: 'Certificate expired', tone: 'offline' };
  }
  if (expires !== null && expires - now < CERT_WARN_MS) {
    return { kind: 'expiring', label: 'Certificate expires soon', tone: 'warn' };
  }
  return { kind: 'enrolled', label: 'Enrolled', tone: 'online' };
}

/**
 * Ids the UI accepts for new edges and sites: they become the certificate CN,
 * the MQTT username and the VPN hostname, so DNS labels only (the API itself
 * accepts any Sparkplug id).
 */
const DNS_LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

/** An error message for `value` as an edge node id or group id, or null when fine. */
export function idProblem(value: string, what: string): string | null {
  if (!value) return `Enter the ${what}.`;
  if (!DNS_LABEL.test(value)) {
    return `The ${what} may use lowercase letters, digits and dashes (not at the ends), at most 63 characters.`;
  }
  return null;
}

/** Re-joins the VPN on the edge VM with a new key (after re-enabling or a lost node). */
export function vpnUpCommand(key: VpnKey, edgeNodeId: string, dir = EDGE_INSTALL_DIR): string {
  return (
    `cd ${dir} && sudo docker compose --profile vpn up -d vpn && ` +
    `sudo docker compose exec vpn tailscale up --login-server=${key.login_server} ` +
    `--authkey=${key.auth_key} --hostname=${edgeNodeId} --force-reauth`
  );
}

/** "in 3 d 4 h" / "2 h ago" for an expiry. */
export function formatExpiry(ts: number, now: number): string {
  const s = Math.round((ts - now) / 1000);
  return s >= 0 ? `in ${formatDuration(s)}` : `${formatDuration(-s)} ago`;
}

const STEP_LABEL: Record<string, string> = {
  mqtt_ban: 'Ban in the MQTT broker',
  mqtt_kick: 'Close MQTT connections',
  vpn_remove: 'Remove from the VPN',
  mqtt_unban: 'Lift the MQTT ban',
};

export function stepLabel(step: string): string {
  return STEP_LABEL[step] ?? step;
}

/** One line per step for the result message. */
export function lifecycleSummary(r: LifecycleResult): string[] {
  return r.steps.map((s) => {
    const status = s.status === 'done' ? 'done' : s.status === 'skipped' ? 'skipped' : 'FAILED';
    return `${stepLabel(s.step)}: ${status}${s.detail ? ` (${s.detail})` : ''}`;
  });
}
